<script setup>
import { ref, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const userInput = ref('')
const chatMessages = ref([])
const isTyping = ref(false)
const chatContainer = ref(null)
const inputRef = ref(null)
const conversationStep = ref(0)

const botResponses = [
  {
    triggers: ['hi', 'hello', 'hey', 'sup', 'yo', 'hola', 'greetings'],
    response: "Hey there! 👋 Thanks for stopping by my portfolio. I'm Braxton's virtual assistant. What brings you here today?",
    options: ['Looking at your projects', 'Want to connect', 'Just exploring']
  },
  {
    triggers: ['project', 'work', 'portfolio', 'looking'],
    response: "Awesome! You can check out my projects by clicking the 'My Projects' folder on the desktop. I've worked on some cool stuff - real-time chat apps, computer vision systems, and more!",
    options: ['Tell me more', 'How can I contact you?', 'Thanks!']
  },
  {
    triggers: ['connect', 'contact', 'reach', 'hire', 'job', 'email', 'linkedin'],
    response: "I'd love to connect! Here's how you can reach me:",
    showLinks: true,
    options: ['Thanks!', 'Tell me about yourself']
  },
  {
    triggers: ['about', 'yourself', 'who', 'tell me'],
    response: "I'm Braxton Freeman - a Software Developer who loves building things! I work with Python, JavaScript, C++, and various web frameworks. When I'm not coding, I'm probably solving LeetCode problems or exploring new tech.",
    options: ['Cool!', 'How can I contact you?']
  },
  {
    triggers: ['explor', 'browse', 'look around', 'just'],
    response: "Feel free to explore! Check out my CV or browse my projects. This whole site is designed like Windows XP - takes you back, right? 😄",
    options: ['Love it!', 'How can I contact you?']
  },
  {
    triggers: ['thanks', 'thank', 'cool', 'awesome', 'nice', 'great', 'love'],
    response: "You're welcome! Feel free to reach out anytime. Don't forget to connect on LinkedIn! Have a great day! 🚀",
    showLinks: true,
    final: true
  },
  {
    triggers: ['more'],
    response: "I've built real-time communication platforms with WebSockets, tracking systems with interactive maps, hospital triage systems in C++/Qt, and I'm currently working on a weapon detection system using computer vision. Check out 'My Projects' for all the details!",
    options: ['Impressive!', 'How can I contact you?']
  }
]

const defaultResponse = {
  response: "I'm not sure I understood that, but I'd love to help! You can ask me about my projects, how to contact me, or just say hi! 😊",
  options: ['Tell me about your projects', 'How can I contact you?', 'Hi!']
}

const scrollToBottom = async () => {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
}

const addBotMessage = async (text, showLinks = false, options = []) => {
  isTyping.value = true
  await scrollToBottom()

  // Simulate typing delay
  await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 700))

  isTyping.value = false
  chatMessages.value.push({
    type: 'bot',
    text,
    showLinks,
    options,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  })
  await scrollToBottom()
}

const findResponse = (input) => {
  const lowerInput = input.toLowerCase()
  for (const resp of botResponses) {
    if (resp.triggers.some(trigger => lowerInput.includes(trigger))) {
      return resp
    }
  }
  return defaultResponse
}

const sendMessage = async () => {
  const message = userInput.value.trim()
  if (!message) return

  chatMessages.value.push({
    type: 'user',
    text: message,
    timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  })
  userInput.value = ''
  await scrollToBottom()

  const response = findResponse(message)
  await addBotMessage(response.response, response.showLinks || false, response.options || [])

  conversationStep.value++
}

const selectOption = async (option) => {
  userInput.value = option
  await sendMessage()
}

onMounted(async () => {
  // Initial greeting after a short delay
  await new Promise(resolve => setTimeout(resolve, 500))
  await addBotMessage(
    "Hey! 👋 Welcome to my Windows XP portfolio! I'm Braxton's virtual assistant. How can I help you today?",
    false,
    ['Looking at your projects', 'Want to connect', 'Just exploring']
  )
  inputRef.value?.focus()
})
</script>

<template>
  <div class="h-full w-full flex flex-col bg-[#d4d0c8] font-trebuchet-pixel text-md">
    <!-- MSN Header Bar -->
    <div class="bg-gradient-to-r from-[#4b9cd3] via-[#6bb3da] to-[#4b9cd3] px-2 py-1 flex items-center gap-2">
      <div class="flex items-center gap-1">
        <!-- MSN Butterfly Icon -->
        <svg width="16" height="16" viewBox="0 0 24 24" class="text-white">
          <path fill="currentColor" d="M12 2C8.5 2 5 4 5 8c0 2.5 1.5 4.5 3 6-1.5 1.5-3 3.5-3 6 0 4 3.5 6 7 6s7-2 7-6c0-2.5-1.5-4.5-3-6 1.5-1.5 3-3.5 3-6 0-4-3.5-6-7-6z"/>
        </svg>
        <span class="text-white font-bold text-xs">MSN Messenger</span>
      </div>
    </div>

    <!-- Contact Info Bar -->
    <div class="bg-[#ece9d8] border-b border-[#aca899] px-2 py-0.5 flex items-center gap-1.5">
      <div class="w-6 h-6 rounded-sm flex items-center justify-center text-white font-bold text-[10px] shadow-sm" style="background: #6B8E23;">
        B
      </div>
      <div class="flex flex-col leading-tight">
        <span class="font-semibold text-[#333] text-sm">Braxton Freeman</span>
        <span class="text-[#666] flex items-center gap-1 text-xs">
          <span class="w-1.5 h-1.5 rounded-full bg-[#6bb700]"></span>
          Online
        </span>
      </div>
    </div>

    <!-- Chat Area -->
    <div
      ref="chatContainer"
      class="flex-1 overflow-y-auto bg-white p-1.5 space-y-2"
    >
      <div v-for="(msg, index) in chatMessages" :key="index" class="flex flex-col">
        <!-- User Message -->
        <div v-if="msg.type === 'user'" class="flex justify-end">
          <div class="max-w-[75%] bg-[#dcf8c6] rounded-lg px-2 py-1 shadow-sm">
            <p class="text-[#333] font-trebuchet-pixel text-md">{{ msg.text }}</p>
            <span class="text-[10px] text-[#999] float-right mt-1">{{ msg.timestamp }}</span>
          </div>
        </div>

        <!-- Bot Message -->
        <div v-else class="flex flex-col items-start gap-1">
          <div class="flex items-start gap-1.5 max-w-[85%]">
            <div class="w-5 h-5 rounded-sm flex items-center justify-center text-white font-bold text-[8px] flex-shrink-0 mt-0.5" style="background: #6B8E23;">
              B
            </div>
            <div class="bg-[#f0f0f0] rounded-lg px-2 py-1 shadow-sm">
              <p class="text-[#333] whitespace-pre-line font-trebuchet-pixel text-md">{{ msg.text }}</p>

              <!-- Contact Links -->
              <div v-if="msg.showLinks" class="mt-2 space-y-1.5">
                <a
                  href="https://linkedin.com/in/braxtonfreeman1010"
                  target="_blank"
                  class="flex items-center gap-1.5 bg-[#0077b5] text-white px-2 py-1 rounded text-sm hover:bg-[#005582] transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                  </svg>
                  LinkedIn
                </a>
                <a
                  href="mailto:thisisbrax@gmail.com"
                  target="_blank"
                  class="flex items-center gap-1.5 bg-[#ea4335] text-white px-2 py-1 rounded text-sm hover:bg-[#c5221f] transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                  Email
                </a>
                <a
                  href="https://github.com/bfreeman1010"
                  target="_blank"
                  class="flex items-center gap-1.5 bg-[#333] text-white px-2 py-1 rounded text-sm hover:bg-[#555] transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33s1.71.11 2.5.33c1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z"/>
                  </svg>
                  GitHub
                </a>
              </div>

              <span class="text-[10px] text-[#999] float-right mt-1 clear-both">{{ msg.timestamp }}</span>
            </div>
          </div>

          <!-- Quick Reply Options -->
          <div v-if="msg.options && msg.options.length > 0 && index === chatMessages.length - 1" class="flex flex-wrap gap-1.5 ml-6 mt-0.5">
            <button
              v-for="option in msg.options"
              :key="option"
              @click="selectOption(option)"
              class="bg-white border border-[#0078d7] text-[#0078d7] px-2.5 py-0.5 rounded-full text-sm hover:bg-[#0078d7] hover:text-white transition-colors cursor-pointer"
            >
              {{ option }}
            </button>
          </div>
        </div>
      </div>

      <!-- Typing Indicator -->
      <div v-if="isTyping" class="flex items-center gap-1.5">
        <div class="w-5 h-5 rounded-sm flex items-center justify-center text-white font-bold text-[8px]" style="background: #6B8E23;">
          B
        </div>
        <div class="bg-[#f0f0f0] rounded-lg px-2 py-1">
          <div class="flex gap-1">
            <span class="w-2 h-2 bg-[#999] rounded-full animate-bounce" style="animation-delay: 0ms"></span>
            <span class="w-2 h-2 bg-[#999] rounded-full animate-bounce" style="animation-delay: 150ms"></span>
            <span class="w-2 h-2 bg-[#999] rounded-full animate-bounce" style="animation-delay: 300ms"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="bg-[#ece9d8] border-t border-[#aca899] p-1.5">
      <div class="flex gap-1.5">
        <input
          ref="inputRef"
          v-model="userInput"
          @keyup.enter="sendMessage"
          type="text"
          :placeholder="t('msn.inputPlaceholder')"
          class="flex-1 px-1.5 py-1 border border-[#7f9db9] rounded font-trebuchet-pixel text-md focus:outline-none focus:border-[#0078d7]"
        />
        <button
          @click="sendMessage"
          class="bg-gradient-to-b from-[#ffffff] to-[#e5e5e5] border border-[#aca899] px-4 py-1 rounded font-trebuchet-pixel text-md hover:from-[#e5f3ff] hover:to-[#c5dfff] active:from-[#c5dfff] active:to-[#e5f3ff] cursor-pointer"
        >
          {{ t('msn.send') }}
        </button>
      </div>
      <div class="mt-1 text-[10px] text-[#666] text-center">
        {{ t('msn.disclaimer') }}
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes bounce {
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}
</style>
